// ── imports explicites (maintenus à la main, comme les autres hooks) ──
import { useEffect, useRef, useState } from "../00-boot.jsx";
import { draftIsStale, draftRecordFromForm, formFromDraftRecord } from "../03-domain-rules.jsx";
// ═══ usePublishDraft — LE BROUILLON DU DÉPÔT (chantier 2 post-Lot A, 01/09/2026) ═══
//
//  PREMIER accès IndexedDB de l'app (mesuré : aucun autre usage). Pourquoi IDB et
//  pas localStorage : les Blobs photo (vague 3) et le _file vidéo y entrent
//  NATIVEMENT, sans base64 ×1,33 ni plafond 5 Mo. Wrapper promesse maison (~30
//  lignes, zéro dépendance) ; CHAQUE accès sous try/catch — une IDB indisponible
//  (navigation privée iOS, quota, corruption) rend `ready:false` et le formulaire
//  retombe INTÉGRALEMENT sur le dialogue de la vague 2. La fonctionnalité se
//  dégrade vers le comportement d'hier, jamais vers de la perte silencieuse.
//
//  CONTRAT (un seul brouillon, par uid, CRÉATION seulement) :
//    · autosave débouncé 800 ms quand l'empreinte a bougé depuis le dernier écrit ;
//    · flush IMMÉDIAT sur visibilitychange→hidden et pagehide (le vrai cycle de
//      vie mobile — beforeunload ne sert plus la création et pénalise le bfcache) ;
//    · jamais d'écriture pendant submitting (on sauverait un envoi en cours) ;
//    · restauration UNE fois au montage (schéma inconnu ou TTL dépassé → écarté
//      ET purgé) ; suppression sur publication réussie / « Supprimer » du toast /
//      « Recommencer à zéro ».
//
//  Per-user-state-guard : AUCUN état de module ici — tout vit dans le hook ou
//  dans IndexedDB, clé = uid (pas de fuite entre comptes ; survit au
//  logout/login du même compte, c'est voulu).

const IDB_DB="lanac-drafts", IDB_STORE="publish", IDB_V=1;
const idbOpen=()=>new Promise((res,rej)=>{
  try{
    if(typeof indexedDB==="undefined"||!indexedDB) return rej(new Error("no idb"));
    const rq=indexedDB.open(IDB_DB,IDB_V);
    rq.onupgradeneeded=()=>{try{rq.result.createObjectStore(IDB_STORE);}catch(e){}};
    rq.onsuccess=()=>res(rq.result);
    rq.onerror=()=>rej(rq.error||new Error("idb open"));
    rq.onblocked=()=>rej(new Error("idb blocked"));
  }catch(e){rej(e);}
});
const idbCall=async(mode,fn)=>{
  const db=await idbOpen();
  try{
    return await new Promise((res,rej)=>{
      const tx=db.transaction(IDB_STORE,mode);
      const rq=fn(tx.objectStore(IDB_STORE));
      tx.oncomplete=()=>res(rq&&("result" in rq)?rq.result:undefined);
      tx.onerror=()=>rej(tx.error||new Error("idb tx"));
      tx.onabort=()=>rej(tx.error||new Error("idb abort"));
    });
  }finally{try{db.close();}catch(e){}}
};
// null = pas de brouillon OU IDB muette — le hook distingue les deux par la SONDE.
const draftRead=uid=>idbCall("readonly",st=>st.get(String(uid)));
const draftWrite=(uid,rec)=>idbCall("readwrite",st=>st.put(rec,String(uid)));
const draftDelete=uid=>idbCall("readwrite",st=>st.delete(String(uid)));

/**
 * @param {{uid:any, enabled:boolean, form:any, blank:any, setForm:Function,
 *          formFingerprint:Function, initialFpRef:{current:any},
 *          submittingRef:{current:boolean}}} cfg
 */
function usePublishDraft(cfg){
  const {uid,enabled,form,blank,setForm,formFingerprint,initialFpRef,submittingRef}=cfg;
  // ready : null = sonde en cours · true = brouillon opérationnel · false = repli dialogue.
  const [ready,setReady]=useState(null);
  // savedAt du brouillon RESTAURÉ — pilote le chip « Brouillon repris · Recommencer ».
  const [restoredAt,setRestoredAt]=useState(null);
  const lastSavedFpRef=useRef(null);   // empreinte du dernier record ÉCRIT (ou restauré)
  const hasDraftRef=useRef(false);     // un brouillon existe-t-il en base ?
  const timerRef=useRef(null);
  const formRef=useRef(form);formRef.current=form;

  // ── Sonde + restauration, UNE fois ─────────────────────────────────────────
  useEffect(()=>{
    if(!enabled||!uid){setReady(false);return;}
    let alive=true;
    (async()=>{
      let rec=null,ok=true;
      try{rec=await draftRead(uid);}catch(e){ok=false;}
      if(!alive) return;
      if(!ok){setReady(false);return;} // IDB muette → repli intégral (dialogue vague 2)
      if(rec&&(draftIsStale(rec)||rec.v==null)){ // périmé ou d'un autre monde → purgé, pas interprété
        try{await draftDelete(uid);}catch(e){}
        rec=null;
      }
      if(rec){
        // ⚠️ COURSE DEBUSQUEE PAR S74 (01/09) : la lecture IDB est asynchrone — un
        // utilisateur rapide peut avoir DEJA tape quand elle aboutit. Ecraser sa
        // saisie fraiche par le brouillon d'hier serait exactement la perte que ce
        // hook combat. La restauration ne s'applique que si le formulaire est
        // ENCORE VIERGE ; sinon on garde le brouillon en base (la saisie en cours
        // l'ecrasera au premier autosave — le plus recent gagne, cote UTILISATEUR).
        const encoreVierge=formFingerprint(formRef.current)===initialFpRef.current;
        const restored=encoreVierge?formFromDraftRecord(rec,blank):null;
        if(!encoreVierge){
          hasDraftRef.current=true; // il existe, on ne le perd pas — mais on ne clobbe pas la saisie
        } else if(restored){
          setForm(restored);
          // L'empreinte de RÉFÉRENCE devient celle du restauré : rouvrir puis refermer
          // sans toucher = fermeture SILENCIEUSE (le brouillon est déjà en base, rien à
          // dire) ; et l'autosave ne réécrit pas un record identique.
          const fp=formFingerprint(restored);
          initialFpRef.current=fp;
          lastSavedFpRef.current=fp;
          hasDraftRef.current=true;
          setRestoredAt(rec.savedAt||0);
        } else if(encoreVierge){
          try{await draftDelete(uid);}catch(e){} // record illisible → purgé
        }
      }
      setReady(true);
    })();
    return ()=>{alive=false;};
  },[enabled,uid]);

  // ── Écriture (partagée par l'autosave, le flush et la fermeture) ───────────
  const saveNow=async()=>{
    if(!enabled||!uid||submittingRef.current) return false;
    const fp=formFingerprint(formRef.current);
    if(fp===lastSavedFpRef.current) return hasDraftRef.current; // rien de neuf
    let rec=draftRecordFromForm(formRef.current);
    // Vidéo (jusqu'à 50 Mo) : si le quota est serré, le brouillon part SANS elle
    // plutôt que d'échouer en entier — et l'appelant peut le dire dans le toast.
    if(rec.video&&rec.video.blob&&navigator.storage&&navigator.storage.estimate){
      try{
        const est=await navigator.storage.estimate();
        if(est&&Number.isFinite(est.quota)&&Number.isFinite(est.usage)&&(est.quota-est.usage)<rec.video.blob.size*2){
          rec={...rec,video:null,videoDropped:true};
        }
      }catch(e){}
    }
    try{
      await draftWrite(uid,rec);
      lastSavedFpRef.current=fp;
      hasDraftRef.current=true;
      return true;
    }catch(e){
      setReady(false); // l'écriture vient d'échouer → on ne PROMET plus le filet
      return false;
    }
  };
  const saveNowRef=useRef(saveNow);saveNowRef.current=saveNow;

  // ── Autosave débouncé + flush cycle de vie ─────────────────────────────────
  useEffect(()=>{
    if(!enabled||ready!==true) return;
    if(submittingRef.current) return;
    if(formFingerprint(form)===lastSavedFpRef.current) return;
    if(formFingerprint(form)===initialFpRef.current&&!hasDraftRef.current) return; // vierge : rien à sauver
    clearTimeout(timerRef.current);
    timerRef.current=setTimeout(()=>{saveNowRef.current();},800);
    return ()=>clearTimeout(timerRef.current);
  },[form,ready,enabled]);
  useEffect(()=>{
    if(!enabled||ready!==true) return;
    const flush=()=>{clearTimeout(timerRef.current);saveNowRef.current();};
    const onVis=()=>{if(document.visibilityState==="hidden")flush();};
    document.addEventListener("visibilitychange",onVis);
    window.addEventListener("pagehide",flush);
    return ()=>{document.removeEventListener("visibilitychange",onVis);window.removeEventListener("pagehide",flush);};
  },[ready,enabled]);

  const clearDraft=async()=>{
    hasDraftRef.current=false;lastSavedFpRef.current=null;setRestoredAt(null);
    try{await draftDelete(uid);}catch(e){}
  };

  return {ready,restoredAt,hasDraft:()=>hasDraftRef.current,saveNow,clearDraft};
}

export { usePublishDraft, draftRead, draftWrite };
